import React, { useMemo, useState, useRef } from "react";
import plur from "plur";
import { Button, Flex, Text, toast } from "@jsq/ds/core";
import { type FormikProps, type Props } from "@jsq/ds/forms";
import { ErrorPage, ErrorType } from "../../../../common/components/ErrorPage";
import { PaymentMethod } from "../../../../common/types/enums";
import {
  type CloneAchPaymentPrefInput,
  useFederatedCloneAchPaymentPrefMutation,
  useUpdatePaymentPrefForAccountsMutation,
} from "../../../../graphql/supergraph.index";
import { toGlobalId } from "../../../../utils/graphql_utils";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { AccountsTable } from "../AccountsTable";
import { useDetermineCreatePaymentPrefMutation } from "../PaymentPrefConfirmation/hooks";
import { convertPaymentPrefDetailToInputType } from "../PaymentPrefConfirmation/utils";
import { PaymentPrefConfirmationDisplayCard } from "../PaymentPrefConfirmationDisplayCard";
import { type ChildComponentProps } from "../types";
import { AchConfirmationForm } from "./AchConfirmationForm";
import { type AchConfirmFormInputs } from "./AchConfirmationForm/types";
import { CREATE_PAYMENT_PREF_ERROR_MESSAGE, LINK_PAYMENT_PREF_ERROR_MESSAGE } from "./strings";
import type { AccountPositions } from "../../Context/types";

export type Test = Props;

export const AccountsPaymentPrefConfirmation: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const {
    accounts,
    paymentPrefDetail,
    paymentPrefId,
    selectedPaymentMethod,
    setPaymentPrefId,
    setUpdatedPositionsPerAccount,
  } = useConsolidatedPaymentPrefContext();

  const [confirming, setConfirming] = useState(false);
  const { createNewPaymentPrefMutation, resultKey } =
    useDetermineCreatePaymentPrefMutation(selectedPaymentMethod);
  const [updatePaymentPrefForAccounts] = useUpdatePaymentPrefForAccountsMutation();
  const [cloneAchPaymentPrefMutation] = useFederatedCloneAchPaymentPrefMutation();
  const positionsCount = useMemo(
    () =>
      accounts?.reduce((acc, account) => acc + (account.node.positions?.edges?.length ?? 0), 0),
    [accounts],
  );
  const achFormRef = useRef<FormikProps<AchConfirmFormInputs>>(null);

  if (!accounts || (!paymentPrefId && !paymentPrefDetail)) {
    return <ErrorPage error={ErrorType.InternalError} />;
  }

  const accountsCount = accounts?.length ?? 0;
  // we just pick one of the arenas as the payment pref would be duplicated on the backend
  const { arenaId } = accounts[0].node;
  const arenaGlobalId = arenaId ? toGlobalId("ArenaObjectType", arenaId) : undefined;

  const description = `This payment method will be applied to ${positionsCount}
    ${plur("investment", positionsCount)} in
    ${accountsCount} investment ${plur("account", accountsCount)}`;

  const confirmChanges = async (signature?: string) => {
    setConfirming(true);

    let localPaymentPrefId = paymentPrefId;
    // Create a new payment pref if user did not select a previous payment pref
    if (!localPaymentPrefId) {
      if (!paymentPrefDetail) {
        return;
      }

      const createPaymentPrefInput = convertPaymentPrefDetailToInputType(
        paymentPrefDetail,
        arenaGlobalId,
        signature,
      );

      // If the input type is empty, paymentPrefDetail couldn't be transformed.
      if (Object.keys(createPaymentPrefInput).length === 0) {
        toast.error("Could not create payment instruction. Please try again later");
        setConfirming(false);
        return;
      }

      try {
        const { data } = await createNewPaymentPrefMutation({
          variables: {
            // @ts-expect-error type will match based on input from context
            input: createPaymentPrefInput,
          },
        });

        const newPaymentPrefId: string | undefined =
          // @ts-expect-error type will match based on input from context
          (data?.[resultKey] as { paymentPref: { id: string } })?.paymentPref.id;

        if (!newPaymentPrefId) {
          toast.error(CREATE_PAYMENT_PREF_ERROR_MESSAGE);
          setConfirming(false);
          return;
        }

        setPaymentPrefId(newPaymentPrefId);
        localPaymentPrefId = newPaymentPrefId;
      } catch (e) {
        toast.error(CREATE_PAYMENT_PREF_ERROR_MESSAGE);
        setConfirming(false);
        return;
      }
    } else if (selectedPaymentMethod === PaymentMethod.ACH) {
      // On the off chance the arena is not available we can't clone the payment pref.
      if (!arenaGlobalId) {
        toast.error("Cannot Create payment prefs. Please try again later");
        setConfirming(false);
        return;
      }
      const clonePaymentPrefInput: CloneAchPaymentPrefInput = {
        arenaId: arenaGlobalId,
        sourcePaymentPrefId: localPaymentPrefId,
        signatureFullName: signature ?? "",
      };
      const { data: cloneMutationData } = await cloneAchPaymentPrefMutation({
        variables: { input: clonePaymentPrefInput },
      });
      const newPaymentPrefId = cloneMutationData?.cloneAchPaymentPref?.paymentPref?.id;

      if (!newPaymentPrefId) {
        toast.error(CREATE_PAYMENT_PREF_ERROR_MESSAGE);
        setConfirming(false);
        return;
      }
      setPaymentPrefId(newPaymentPrefId);
      localPaymentPrefId = newPaymentPrefId;
    }

    try {
      const associatePaymentPrefResult = await updatePaymentPrefForAccounts({
        variables: {
          objectInput: {
            paymentPrefId: localPaymentPrefId,
            accountIds: accounts.map((account) => account.node.id),
          },
        },
      });

      const associatePaymentPrefData =
        associatePaymentPrefResult?.data?.updatePaymentPrefForAccounts;

      // All mutations return MutationErrorResponse if the mutation failed.
      if (
        !associatePaymentPrefData ||
        associatePaymentPrefData?.__typename === "MutationErrorResponse"
      ) {
        toast.error(LINK_PAYMENT_PREF_ERROR_MESSAGE);
        setConfirming(false);
        return;
      }

      const positions = associatePaymentPrefData?.changedPositions;

      const accountPositions = positions.reduce<AccountPositions>(
        (acc: AccountPositions, position) => {
          if (position.account) {
            return {
              ...acc,
              [position.account.id]: acc[position.account.id]
                ? [...acc[position.account.id], position.id]
                : [position.id],
            };
          }
          return acc;
        },
        {},
      );

      setUpdatedPositionsPerAccount(accountPositions);
    } catch (e) {
      toast.error(LINK_PAYMENT_PREF_ERROR_MESSAGE);
      setConfirming(false);
      return;
    }

    setConfirming(false);
    onContinueClick();
  };

  const onContinueHandler = async () => {
    if (selectedPaymentMethod === PaymentMethod.ACH) {
      if (!achFormRef.current) {
        return;
      }
      await achFormRef.current.submitForm();
    } else {
      await confirmChanges();
    }
  };

  const onAchFormSubmit = async (achPaymentPrefWithSignature?: string) => {
    await confirmChanges(achPaymentPrefWithSignature);
  };

  return (
    <>
      <Text className="stacked-lg" variant="h3">
        Confirm updated payment instruction
      </Text>
      <Text className="stacked-md">{description}</Text>
      <div className="stacked-lg">
        <PaymentPrefConfirmationDisplayCard />
      </div>
      <div className="stacked-lg">
        <AccountsTable accounts={accounts} />
      </div>
      {selectedPaymentMethod === PaymentMethod.ACH && (
        <AchConfirmationForm
          accounts={accounts}
          formRef={achFormRef}
          onSubmit={onAchFormSubmit}
        />
      )}
      <Flex flexDirection="row-reverse" gap={8}>
        <Button isLoading={confirming} onClick={() => void onContinueHandler()}>
          Confirm
        </Button>
        <Button variant="neutral" onClick={onBackClick}>
          {onBackButtonName}
        </Button>
      </Flex>
    </>
  );
};
